import { AdminAlertBanner } from "@/components/AdminAlertBanner";
import { AdminReviews } from "@/components/AdminReviews";
import { AdminNewsletter } from "@/components/AdminNewsletter";
import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Inbox, MessageSquareQuote, Trash2, Check, Mail, Plus, ShieldCheck, Reply, Send, Star, HelpCircle, CalendarClock, Clock, Piano, Gift } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FeedbackReview, TutorInbox } from "@/components/AdminFeedback";
import { TrackEditor } from "@/components/TrackEditor";
import { AdminDevices } from "@/components/MyDevices";
import { AdminWeeklyProgress } from "@/components/AdminWeeklyProgress";

const REPLY_TEMPLATES = [
  { label: "Booked", body: (n) => `Hi ${n?.split(" ")[0] || "there"},\n\nGreat news — I've booked your session. I'll follow up with the exact date, time and joining details shortly.\n\nLooking forward to it!\nJT` },
  { label: "Times offered", body: (n) => `Hi ${n?.split(" ")[0] || "there"},\n\nThanks for getting in touch! I have a few slots free this week — would any of these suit you?\n\n• Tuesday 4:00pm\n• Wednesday 6:30pm\n• Saturday 10:00am\n\nJust let me know which works and I'll confirm.\nJT` },
  { label: "More info", body: (n) => `Hi ${n?.split(" ")[0] || "there"},\n\nThanks for your enquiry! Could you tell me a little more about your current level and what you'd most like to work on? That'll help me tailor the session.\n\nJT` },
];

const SCHOOL_TEMPLATES = [
  { label: "Quote", body: (c) => { const n = c.classes || 1; return `Hi ${(c.name || "there").split(" ")[0]},\n\nThank you for your interest in The JT Hub for ${c.school_name || "your school"}.\n\nA classroom licence is £400 per class, per course, and unlocks the full 10-lesson course for every student in that class. For ${n} class${n === 1 ? "" : "es"} on one course, that comes to £${(n * 400).toLocaleString("en-GB")}.\n\nLet me know which instruments and levels you'd like and I'll get everything set up.\n\nBest wishes,\nJT`; } },
  { label: "Book a call", body: (c) => `Hi ${(c.name || "there").split(" ")[0]},\n\nThanks for your enquiry! I'd love to have a quick 15-minute call to understand your classes and recommend the right courses. Could you send me a couple of days and times that work for you?\n\nBest wishes,\nJT` },
  { label: "Brochure", body: (c) => `Hi ${(c.name || "there").split(" ")[0]},\n\nThanks for getting in touch. You can download our school brochure here:\n${window.location.origin}/media/jt-hub-school-brochure.pdf\n\nIt covers the £400 classroom licence, what's included, and how to sign up. Happy to answer any questions.\n\nBest wishes,\nJT` },
  { label: "How to start", body: (c) => `Hi ${(c.name || "there").split(" ")[0]},\n\nGetting started is simple:\n\n1. Create a teacher account at ${window.location.origin}/register?role=teacher using your school email\n2. Enter the 6-digit code we email to your school address\n3. Create a class and buy a £400 classroom licence from its Licences tab\n4. Share the join code with your students\n\nAny questions, just reply to this email.\n\nBest wishes,\nJT` },
];

const EnquiryCard = ({ c, templates }) => {
  const [replying, setReplying] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(c.handled);
  const firstName = (c.name || "there").split(" ")[0];
  const allTemplates = [
    ...(c.kind === "school" ? SCHOOL_TEMPLATES.map((t) => ({ label: t.label, text: t.body(c) })) : REPLY_TEMPLATES.map((t) => ({ label: t.label, text: t.body(c.name) }))),
    ...templates.map((t) => ({ label: t.label, text: t.body.replace(/\{name\}/g, firstName) })),
  ];

  const toggle = async () => {
    try {
      await api.patch(`/contact/${c.id}`, { handled: !handled });
      setHandled(!handled);
    } catch (e) { toast.error("Could not update"); }
  };

  const sendReply = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/contact/${c.id}/reply`, { message: msg });
      toast.success(`Reply emailed to ${c.email}`);
      setReplying(false); setMsg(""); setHandled(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reply"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border p-5 ${handled ? "border-border bg-secondary/40" : "border-border bg-card"}`} data-testid={`enquiry-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-medium text-foreground">{c.name}</p>
          {c.kind === "school" && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary" data-testid={`enquiry-school-badge-${c.id}`}>School quote</span>}
          {c.kind === "school" && !handled && Date.now() - new Date(c.created_at).getTime() > 2 * 864e5 && (
            <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-500" data-testid={`enquiry-overdue-badge-${c.id}`}>Waiting 2+ days</span>
          )}
          <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs text-primary hover:underline"><Mail size={12} /> {c.email}</a>
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">{(c.created_at || "").slice(0, 10)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{c.message}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={() => setReplying((r) => !r)} data-testid={`reply-toggle-${c.id}`} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-primary transition-colors hover:border-primary">
          <Reply size={13} /> Reply
        </button>
        <button onClick={toggle} data-testid={`enquiry-handled-${c.id}`} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${handled ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "border border-border text-muted-foreground hover:border-primary"}`}>
          <Check size={13} /> {handled ? "Handled" : "Mark handled"}
        </button>
      </div>
      {replying && (
        <div className="mt-3" data-testid={`reply-box-${c.id}`}>
          <div className="mb-2 flex flex-wrap gap-2" data-testid={`reply-templates-${c.id}`}>
            {allTemplates.map((t) => (
              <button key={t.label} type="button" onClick={() => setMsg(t.text)} data-testid={`reply-template-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                {t.label}
              </button>
            ))}
          </div>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} maxLength={4000} data-testid={`reply-message-${c.id}`}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder={`Write your reply to ${c.name}…`} />
          <Button onClick={sendReply} disabled={busy} data-testid={`send-reply-${c.id}`} className="mt-2 rounded-full bg-primary text-foreground hover:bg-primary/90">
            <Send size={14} className="mr-1.5" /> {busy ? "Sending…" : "Send reply"}
          </Button>
        </div>
      )}
    </div>
  );
};

const Enquiries = () => {
  const [items, setItems] = useState(null);
  const [templates, setTemplates] = useState([]);
  const load = useCallback(() => api.get("/contact").then(({ data }) => setItems(data.requests || [])).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/reply-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {}); }, []);

  return (
    <section data-testid="admin-enquiries">
      <div className="flex items-center gap-2"><Inbox size={18} className="text-primary" /><h2 className="font-serif text-2xl">Booking enquiries</h2></div>
      {items === null ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="enquiries-empty">No enquiries yet. 1:1 requests and school quote requests appear here.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((c) => <EnquiryCard key={c.id} c={c} templates={templates} />)}
        </div>
      )}
    </section>
  );
};

const ReplyTemplatesManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", body: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/reply-templates").then(({ data }) => setItems(data.templates || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reply-templates", form);
      setItems((l) => [...l, data]);
      setForm({ label: "", body: "" });
      toast.success("Template saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/reply-templates/${id}`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <section className="mt-14" data-testid="admin-reply-templates">
      <div className="flex items-center gap-2"><Reply size={18} className="text-primary" /><h2 className="font-serif text-2xl">Reply templates</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Your own canned replies, shown as buttons in the enquiry reply box. Use <span className="font-mono">{"{name}"}</span> to insert the enquirer's first name.</p>
      <form onSubmit={add} className="mt-4 space-y-3 rounded-2xl border border-border bg-card p-5">
        <div><Label className="text-muted-foreground">Button label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="template-label" className="mt-1 border-border bg-background" placeholder="e.g. Payment link" maxLength={40} /></div>
        <div><Label className="text-muted-foreground">Reply text</Label><textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} maxLength={2000} data-testid="template-body" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" placeholder="Hi {name}, here is the link to pay…" /></div>
        <Button type="submit" disabled={busy} data-testid="add-template" className="rounded-full bg-primary text-foreground hover:bg-primary/90"><Plus size={15} className="mr-1" /> {busy ? "Saving…" : "Save template"}</Button>
      </form>
      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          {items.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3" data-testid={`template-${t.id}`}>
              <div><p className="text-sm font-medium">{t.label}</p><p className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">{t.body}</p></div>
              <button onClick={() => del(t.id)} data-testid={`delete-template-${t.id}`} className="rounded-full p-1.5 text-red-600 dark:text-red-400 transition-colors hover:bg-red-500/15"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const Stars = ({ value = 5, onChange }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n} type="button" onClick={onChange ? () => onChange(n) : undefined}
        data-testid={onChange ? `star-${n}` : undefined}
        className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}
        aria-label={`${n} star`}
      >
        <Star size={onChange ? 20 : 14} className={n <= value ? "fill-primary text-primary" : "text-muted-foreground"} />
      </button>
    ))}
  </span>
);

const Avatar = ({ name, photo }) => (
  photo ? (
    <img src={photo} alt={name} className="h-10 w-10 rounded-full object-cover" />
  ) : (
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-serif text-sm text-primary">
      {(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
    </span>
  )
);

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ quote: "", name: "", role: "", rating: 5, photo_url: "", audience: "student" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/testimonials?audience=all").then(({ data }) => setItems(data.testimonials || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/testimonials", form);
      setItems((l) => [data, ...l]);
      setForm({ quote: "", name: "", role: "", rating: 5, photo_url: "", audience: form.audience });
      toast.success("Testimonial published");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/testimonials/${id}`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <section className="mt-14" data-testid="admin-testimonials">
      <div className="flex items-center gap-2"><MessageSquareQuote size={18} className="text-primary" /><h2 className="font-serif text-2xl">Testimonials</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Student testimonials show on the About page. Teacher testimonials show in the Teacher Rates section on Pricing.</p>
      <form onSubmit={add} className="mt-4 space-y-3 rounded-2xl border border-border bg-card p-5">
        <div className="flex gap-2">
          {[["student", "Student"], ["teacher", "Teacher"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setForm({ ...form, audience: v })} data-testid={`testimonial-audience-${v}`}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${form.audience === v ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}>
              {l}
            </button>
          ))}
        </div>
        <div>
          <Label className="text-muted-foreground">Quote</Label>
          <textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3} maxLength={600} data-testid="testimonial-quote" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" placeholder="What the student said…" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-muted-foreground">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name" className="mt-1 border-border bg-background" placeholder="Student name" /></div>
          <div><Label className="text-muted-foreground">Role / instrument</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="testimonial-role" className="mt-1 border-border bg-background" placeholder="e.g. Adult beginner · Piano" /></div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-muted-foreground">Rating</Label><div className="mt-2"><Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} /></div></div>
          <div><Label className="text-muted-foreground">Photo URL (optional, https)</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} data-testid="testimonial-photo" className="mt-1 border-border bg-background" placeholder="https://…" /></div>
        </div>
        <Button type="submit" disabled={busy} data-testid="add-testimonial" className="rounded-full bg-primary text-foreground hover:bg-primary/90"><Plus size={15} className="mr-1" /> {busy ? "Publishing…" : "Publish testimonial"}</Button>
      </form>
      {items.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {items.map((t) => (
            <div key={t.id} className="relative rounded-2xl border border-border bg-card p-5" data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex items-center gap-2">
                <Stars value={t.rating || 5} />
                {t.audience === "teacher" && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary" data-testid={`testimonial-teacher-badge-${t.id}`}>Teacher</span>}
              </div>
              <blockquote className="mt-2 text-sm text-muted-foreground">"{t.quote}"</blockquote>
              <div className="mt-3 flex items-center gap-3">
                <Avatar name={t.name} photo={t.photo_url} />
                <div><p className="text-sm font-medium">{t.name}</p><p className="text-xs text-muted-foreground">{t.role}</p></div>
              </div>
              <button onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`} className="absolute right-3 top-3 rounded-full p-1.5 text-red-600 dark:text-red-400 transition-colors hover:bg-red-500/15"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const FaqManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", order: 0 });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/faqs").then(({ data }) => setItems(data.faqs || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || !form.answer.trim()) return;
    setBusy(true);
    try {
      await api.post("/faqs", { ...form, order: Number(form.order) || 0 });
      setForm({ question: "", answer: "", order: 0 });
      toast.success("FAQ added");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/faqs/${id}`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <section className="mt-14" data-testid="admin-faqs">
      <div className="flex items-center gap-2"><HelpCircle size={18} className="text-primary" /><h2 className="font-serif text-2xl">FAQs</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">These appear on the Landing and Pricing pages. If none are added, a sensible default set is shown.</p>
      <form onSubmit={add} className="mt-5 space-y-3 rounded-2xl border border-border bg-card p-5">
        <div><Label className="text-muted-foreground">Question</Label><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" className="mt-1 border-border bg-background" placeholder="e.g. Do you offer free lessons?" /></div>
        <div><Label className="text-muted-foreground">Answer</Label><textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={3} maxLength={2000} data-testid="faq-answer" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" placeholder="The answer…" /></div>
        <div className="w-28"><Label className="text-muted-foreground">Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="faq-order" className="mt-1 border-border bg-background" /></div>
        <Button type="submit" disabled={busy} data-testid="add-faq" className="rounded-full bg-primary text-foreground hover:bg-primary/90"><Plus size={15} className="mr-1" /> {busy ? "Adding…" : "Add FAQ"}</Button>
      </form>
      <div className="mt-6 space-y-3">
        {items.map((f) => (
          <div key={f.id} className="relative rounded-2xl border border-border bg-card p-5 pr-12" data-testid={`admin-faq-${f.id}`}>
            <p className="font-medium">{f.question}</p>
            <p className="mt-1 text-sm text-muted-foreground">{f.answer}</p>
            <button onClick={() => del(f.id)} data-testid={`delete-faq-${f.id}`} className="absolute right-3 top-3 rounded-full p-1.5 text-red-600 dark:text-red-400 transition-colors hover:bg-red-500/15"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </section>
  );
};

const SessionsManager = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/one-to-one").then(({ data }) => setItems(data.sessions || [])).catch(() => setItems([])); }, []);

  const paid = (items || []).filter((s) => s.payment_status === "paid");
  const booked = paid.filter((s) => s.booking_slot);
  const awaiting = paid.filter((s) => !s.booking_slot);

  const Row = ({ s }) => (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`session-${s.session_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="font-medium text-foreground">{s.name || "Student"}</p>
          {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-1 text-xs text-primary hover:underline"><Mail size={12} /> {s.email}</a>}
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">{(s.created_at || "").slice(0, 10)}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">{s.hours} hour{s.hours === 1 ? "" : "s"}</span>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">£{Number(s.amount || 0).toFixed(0)}</span>
        {s.booking_slot ? (
          <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-600 dark:text-emerald-400"><CalendarClock size={12} /> {s.booking_slot}</span>
        ) : (
          <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary"><Clock size={12} /> Awaiting scheduling</span>
        )}
      </div>
      {s.booking_note && <p className="mt-2 text-xs text-muted-foreground">Note: {s.booking_note}</p>}
    </div>
  );

  return (
    <section className="mt-14" data-testid="admin-sessions">
      <div className="flex items-center gap-2"><CalendarClock size={18} className="text-primary" /><h2 className="font-serif text-2xl">1:1 sessions with Jason</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Paid 1:1 bookings. Scheduled sessions show the client's chosen slot; awaiting ones are paid but not yet scheduled.</p>
      {items === null ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : paid.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="sessions-empty">No paid 1:1 sessions yet.</p>
      ) : (
        <div className="mt-4 space-y-6">
          {booked.length > 0 && (
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Scheduled ({booked.length})</p>
              <div className="mt-3 space-y-3" data-testid="sessions-scheduled">{booked.map((s) => <Row key={s.session_id} s={s} />)}</div>
            </div>
          )}
          {awaiting.length > 0 && (
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Awaiting scheduling ({awaiting.length})</p>
              <div className="mt-3 space-y-3" data-testid="sessions-awaiting">{awaiting.map((s) => <Row key={s.session_id} s={s} />)}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const ReferralsManager = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/referrals").then(({ data }) => setD(data)).catch(() => setD({ totals: {}, referrers: [], purchases: [] })); }, []);
  const T = ({ v, l, id }) => (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={id}><p className="font-mono text-2xl text-primary">{v || 0}</p><p className="text-xs text-muted-foreground">{l}</p></div>
  );
  return (
    <section className="mt-14" data-testid="admin-referrals">
      <div className="flex items-center gap-2"><Gift size={18} className="text-primary" /><h2 className="font-serif text-2xl">Referrals & free 1:1s</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Who is referring friends, which referred purchases were paid, and free 1:1 lessons given and used.</p>
      {d === null ? <p className="mt-4 text-sm text-muted-foreground">Loading…</p> : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <T v={d.totals.referrers} l="Members sharing a code" id="ref-total-referrers" />
            <T v={d.totals.paid} l="Referred purchases paid" id="ref-total-paid" />
            <T v={d.totals.free_given} l="Free 1:1s earned" id="ref-total-given" />
            <T v={d.totals.free_used} l="Free 1:1s booked" id="ref-total-used" />
          </div>
          {d.referrers.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="referrals-empty">No members have generated a referral code yet.</p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card" data-testid="referrers-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  <th className="p-3">Member</th><th className="p-3">Code</th><th className="p-3">Invited</th><th className="p-3">Paid</th><th className="p-3">Free left</th><th className="p-3">Free used</th></tr></thead>
                <tbody>{d.referrers.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`referrer-${r.code}`}>
                    <td className="p-3"><p className="text-foreground">{r.name}</p><p className="text-xs text-muted-foreground">{r.email}</p></td>
                    <td className="p-3 font-mono text-primary">{r.code}</td><td className="p-3">{r.invited}</td><td className="p-3">{r.paid}</td>
                    <td className="p-3">{r.free_available}</td><td className="p-3">{r.free_used}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
          {d.purchases.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="referred-purchases">
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Referred purchases ({d.purchases.length})</p>
              {d.purchases.map((p) => (
                <div key={p.session_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid={`referred-purchase-${p.session_id}`}>
                  <span className="text-foreground">{p.buyer || "Student"} <span className="text-muted-foreground">· {p.tier}</span></span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground"><span className="font-mono text-primary">{p.code}</span>£{Number(p.amount || 0).toFixed(0)}<span>{(p.created_at || "").slice(0, 10)}</span></span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

const WaitlistManager = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/waitlist").then(({ data }) => setItems(data.entries || [])).catch(() => setItems([])); }, []);
  return (
    <section className="mt-14" data-testid="admin-waitlist">
      <div className="flex items-center gap-2"><Piano size={18} className="text-primary" /><h2 className="font-serif text-2xl">Piano course waitlist</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">People who registered interest in the upcoming piano course.</p>
      {items === null ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="waitlist-empty">No sign-ups yet.</p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="waitlist-list">
          {items.map((w) => (
            <div key={w.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`waitlist-entry-${w.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground">{w.name}</p>
                  {w.email && <a href={`mailto:${w.email}`} className="flex items-center gap-1 text-xs text-primary hover:underline"><Mail size={12} /> {w.email}</a>}
                </div>
                <span className="font-mono text-[11px] text-muted-foreground">{(w.created_at || "").slice(0, 10)}</span>
              </div>
              {w.focus && <div className="mt-2"><span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{w.focus}</span></div>}
              {w.message && <p className="mt-2 text-xs text-muted-foreground">{w.message}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};


export default function AdminConsole() {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-background"><ShieldCheck className="animate-pulse text-primary" size={40} /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-4xl px-4 sm:px-8 py-10 lg:py-14">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Admin</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Console</h1>
        <AdminAlertBanner />
        <Link to="/admin/backing" data-testid="admin-backing-link" className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Backing tracks — upload your recordings →</Link>
        <Link to="/admin/scores" data-testid="admin-scores-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Transpose a score →</Link>
        <Link to="/admin/more" data-testid="admin-more-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Band bookings & interest →</Link>
        <Link to="/admin/helper" data-testid="admin-helper-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Helper questions →</Link>
        <Link to="/admin/terms" data-testid="admin-terms-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Game signs & terms →</Link>
        <Link to="/admin/gallery-stories" data-testid="admin-gallery-stories-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Gallery stories →</Link>
        <Link to="/admin/video-wording" data-testid="admin-video-wording-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Video wording →</Link>
        <Link to="/admin/bank-transfers" data-testid="admin-bank-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Bank transfers →</Link>
        <Link to="/admin/payments" data-testid="admin-payments-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Payment summary →</Link>
        <Link to="/admin/cutdowns" data-testid="admin-cutdowns-link" className="ml-3 mt-4 inline-flex items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">Social video cutdowns →</Link>
        <div className="mt-10"><Enquiries /></div>
        <SessionsManager />
        <ReferralsManager />
        <AdminDevices />
        <AdminWeeklyProgress />
        <WaitlistManager />
        <ReplyTemplatesManager />
        <Testimonials />
        <AdminReviews />
        <AdminNewsletter />
        <FeedbackReview />
        <TutorInbox />
        <TrackEditor />
        <FaqManager />
      </motion.div>
    </div>
  );
}
